import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  FileText, PenLine, CheckCircle, XCircle, Clock, Trophy, ArrowLeft,
  Sun, Moon, User, LogOut, Loader2, BookOpen, Award, ChevronRight,
  History, Sparkles, Brain, Send, Shield, BarChart3, Zap, Eye,
  ChevronLeft
} from 'lucide-react';
import { boardExamsApi } from '../lib/api';
import { toast } from 'sonner';
import { HandwrittenUploader } from '../components/HandwrittenUploader';
import { FontSizeSlider, useExamFontScale } from '../components/exam/FontSizeSlider';

const SUBJECT_ICONS = {
  'Mathematics': '📐', 'Science': '🔬', 'Physics': '⚡', 'Chemistry': '🧪',
  'Biology': '🧬', 'English': '📖', 'Hindi': '📝', 'Social Science': '🌍',
  'History & Civics': '🏛️', 'Geography': '🗺️', 'Computer Science': '💻',
  'Economics': '📊', 'Accountancy': '📒', 'Accounts': '📒', 'Business Studies': '💼',
  'Hindi/Regional': '📝', 'Social Studies': '🌍', 'Hindi/Regional Language': '📝',
  'Computer Studies': '💻', 'Computer Applications': '💻', 'Computer Basics': '💻',
  'Informatics Practices': '💻', 'ICT': '💻', 'Art': '🎨', 'Arts': '🎨',
  'Physical Education': '🏃', 'Civics': '📜', 'History': '🏛️',
  'Political Science': '⚖️', 'Sociology': '👥', 'Psychology': '🧠',
  'Second Language': '📝', 'Regional Language': '📝', 'Commerce': '💼',
  'Physics Basics': '⚡', 'Chemistry Basics': '🧪', 'Biology Basics': '🧬',
  'Language & Literature': '📖', 'Individuals & Societies': '🌍',
  'Algebra (Paper 1)': '📐', 'Geometry (Paper 2)': '📏',
  'Science Part 1 (Physics & Chemistry)': '⚗️', 'Science Part 2 (Biology)': '🧬',
  'History & Political Science': '🏛️',
};

const BOARD_GRADES = {
  CBSE: [5, 6, 7, 8, 9, 10, 11, 12],
  ICSE: [5, 6, 7, 8, 9, 10, 11, 12],
  SSC:  [5, 6, 7, 8, 9, 10],
  HSC:  [11, 12],
  IB:   [5, 6, 7, 8, 9, 10, 11, 12],
  Cambridge: [5, 6, 7, 8, 9, 10, 11, 12],
};

/* Diamond-shaped question number component */
const DiamondNumber = ({ number, status, isActive, onClick, testId }) => {
  // status: 'unattempted' | 'attempted' | 'correct' | 'wrong' | 'current'
  let bg, text;
  switch (status) {
    case 'correct':
      bg = 'bg-green-500'; text = 'text-yellow-300'; break;
    case 'wrong':
      bg = 'bg-red-500'; text = 'text-yellow-300'; break;
    case 'attempted':
      bg = 'bg-[#FFD700]'; text = 'text-white'; break;
    default: // unattempted
      bg = 'bg-sky-400'; text = 'text-blue-800'; break;
  }

  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative w-10 h-10 flex items-center justify-center transform rotate-45 transition-all duration-200 hover:scale-110 ${bg} ${isActive ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''}`}
      style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}
    >
      <span className={`transform -rotate-45 text-xs font-bold ${text}`}>{number}</span>
    </button>
  );
};

export default function ExamModule() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Setup state
  const [view, setView] = useState('setup'); // setup | exam | result | history
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedBoard, setSelectedBoard] = useState(user?.board || 'CBSE');
  const [selectedGrade, setSelectedGrade] = useState(String(user?.grade || 10));
  const [selectedStream, setSelectedStream] = useState('');
  const [availableStreams, setAvailableStreams] = useState([]);
  const [examType, setExamType] = useState('mcq');
  const [examMode, setExamMode] = useState('quick'); // 'quick' | 'real'
  const [questionCount, setQuestionCount] = useState('10');
  const [generating, setGenerating] = useState(false);

  const validGrades = BOARD_GRADES[selectedBoard] || [5,6,7,8,9,10,11,12];

  const handleBoardChange = (board) => {
    setSelectedBoard(board);
    const grades = BOARD_GRADES[board] || [5,6,7,8,9,10,11,12];
    const currentGrade = parseInt(selectedGrade);
    if (!grades.includes(currentGrade)) {
      setSelectedGrade(String(grades[0]));
    }
    setSelectedStream('');
    setAvailableStreams([]);
  };

  // Exam state
  const [currentExam, setCurrentExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Quick mode state - track checked answers
  const [checkedAnswers, setCheckedAnswers] = useState({}); // {qIdx: {correct: bool, explanation: ''}}
  const [expandedSteps, setExpandedSteps] = useState({}); // Phase 40.30 — {qIdx: bool}
  const [fontScale, setFontScale] = useExamFontScale(); // Phase 40.31 — persistent A-/A+ font size

  // Ref for textarea focus preservation
  const writtenAnswerRef = useRef(null);
  const [ocrResults, setOcrResults] = useState({});  // { [qIdx]: {marks_awarded, max_marks, feedback, ...} }
  const wasWritingRef = useRef(false);
  const cursorPosRef = useRef(null);
  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const isAdmin = ['super_admin', 'org_admin', 'faculty'].includes(user?.role);

  useEffect(() => { loadSubjects(); }, [selectedBoard, selectedGrade, selectedStream]);

  const loadSubjects = async () => {
    try {
      const data = await boardExamsApi.getSubjects(selectedBoard, parseInt(selectedGrade), selectedStream || null);
      setSubjects(data.subjects || []);
      setAvailableStreams(data.streams || []);
      setSelectedSubject('');
    } catch {
      setSubjects(['Mathematics', 'Science', 'English']);
      setAvailableStreams([]);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await boardExamsApi.getHistory();
      setHistory(data || []);
    } catch { setHistory([]); }
    setHistoryLoading(false);
  };

  const generateExam = async () => {
    if (!selectedSubject) { toast.error('Please select a subject'); return; }
    setGenerating(true);
    try {
      const exam = await boardExamsApi.generate({
        subject: selectedSubject,
        board: selectedBoard,
        grade: parseInt(selectedGrade),
        exam_type: examType,
        total_questions: parseInt(questionCount),
        duration_minutes: examType === 'mcq' ? 30 : 60,
      });
      setCurrentExam(exam);
      setAnswers({});
      setCheckedAnswers({});
      setCurrentQ(0);
      setResult(null);
      setTimeLeft(exam.duration_minutes * 60);
      setView('exam');
      toast.success('Exam generated!');
    } catch {
      toast.error('Failed to generate exam');
    }
    setGenerating(false);
  };

  // Timer
  useEffect(() => {
    if (view !== 'exam' || timeLeft === null || timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft(p => (p > 0 ? p - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [view, timeLeft]);

  useEffect(() => {
    if (timeLeft === 0 && currentExam && view === 'exam') {
      toast.warning('Time is up!');
      if (examMode === 'real') handleSubmit();
      else setView('result');
    }
  }, [timeLeft]);

  // Restore textarea focus and cursor position after timer re-renders
  useEffect(() => {
    if (wasWritingRef.current && writtenAnswerRef.current && currentExam?.exam_type === 'written') {
      const el = writtenAnswerRef.current;
      if (document.activeElement !== el) {
        el.focus();
      }
      if (cursorPosRef.current !== null) {
        el.selectionStart = cursorPosRef.current;
        el.selectionEnd = cursorPosRef.current;
      }
    }
  });

  const handleMCQAnswer = (qIdx, optionIdx) => {
    if (checkedAnswers[qIdx]) return; // locked in quick mode after check
    setAnswers(p => ({ ...p, [qIdx]: optionIdx }));
  };

  const handleWrittenAnswer = (qIdx, text) => {
    // Save cursor position before React re-render
    if (writtenAnswerRef.current) {
      cursorPosRef.current = writtenAnswerRef.current.selectionStart;
    }
    setAnswers(p => ({ ...p, [qIdx]: text }));
  };

  // Quick Mode: Check single answer
  const checkAnswer = (qIdx) => {
    if (!currentExam) return;
    const q = currentExam.questions[qIdx];
    if (currentExam.exam_type === 'mcq') {
      // Phase 40.23 — the backend fallback bank uses `correct_answer` and the
      // legacy question_bank collection uses `correct`. Support both so the
      // user's selection is never wrongly marked Incorrect just because the
      // key differs between sources.
      const correctIdx = q.correct_answer ?? q.correct;
      const isCorrect = answers[qIdx] === correctIdx;
      setCheckedAnswers(p => ({
        ...p,
        [qIdx]: {
          correct: isCorrect,
          explanation: q.explanation || '',
          correctAnswer: correctIdx,
          // Phase 40.30 — step-by-step derivation payload from backend.
          steps: q.steps || [],
          concept: q.concept || '',
          whyWrong: q.why_wrong || {},
          learningTip: q.learning_tip || '',
        }
      }));
    } else {
      // For written, just show expected answer
      setCheckedAnswers(p => ({
        ...p,
        [qIdx]: {
          correct: null,
          explanation: q.expected_answer || '',
          keywords: q.keywords || [],
        }
      }));
    }
  };

  // Real Mode: Submit all
  const handleSubmit = async () => {
    if (!currentExam) return;
    setSubmitting(true);
    try {
      const answersList = Object.entries(answers).map(([qIdx, ans]) => ({
        question_index: parseInt(qIdx),
        answer: ans,
      }));
      const res = await boardExamsApi.submit({ exam_id: currentExam.exam_id, answers: answersList });
      setResult(res);
      setView('result');
      toast.success('Exam submitted!');
    } catch {
      toast.error('Submission failed');
    }
    setSubmitting(false);
  };

  const formatTime = (s) => {
    if (s === null) return '--:--';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const totalQ = currentExam?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;

  // Get question status for diamond
  const getQStatus = (idx) => {
    if (examMode === 'quick' && checkedAnswers[idx]) {
      if (currentExam?.exam_type === 'mcq') {
        return checkedAnswers[idx].correct ? 'correct' : 'wrong';
      }
      return 'attempted'; // written in quick mode shows as attempted after check
    }
    if (answers[idx] !== undefined && answers[idx] !== '') return 'attempted';
    return 'unattempted';
  };

  // Quick mode score calculation
  const getQuickScore = () => {
    if (!currentExam || currentExam.exam_type !== 'mcq') return null;
    const checked = Object.values(checkedAnswers);
    const correct = checked.filter(c => c.correct === true).length;
    return { correct, total: checked.length, percentage: checked.length > 0 ? Math.round(correct / checked.length * 100) : 0 };
  };

  // ====================== SETUP VIEW ======================
  const SetupView = () => (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Board Exam Simulator Banner */}
      <Link to="/board-exam-simulator" className="block p-4 rounded-xl border-2 border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20 hover:shadow-lg transition-all group" data-testid="simulator-link">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold group-hover:text-emerald-600 transition-colors">Board Exam Simulator</h3>
              <p className="text-xs text-muted-foreground">Realistic board exam format with sections, marks & GAP analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium">NEW</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-emerald-600" />
          </div>
        </div>
      </Link>

      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Generate Your Exam</h2>
        <p className="text-sm text-muted-foreground">Choose board, grade, subject, and exam mode. AI will create a personalized exam.</p>
      </div>

      {/* Board & Grade Selectors */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Board</label>
          <Select value={selectedBoard} onValueChange={handleBoardChange}>
            <SelectTrigger data-testid="board-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['CBSE', 'ICSE', 'SSC', 'HSC', 'IB', 'Cambridge'].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Grade</label>
          <Select value={selectedGrade} onValueChange={(v) => { setSelectedGrade(v); setSelectedStream(''); }}>
            <SelectTrigger data-testid="grade-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {validGrades.map(g => <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {availableStreams.length > 0 && (
          <div>
            <label className="text-sm font-medium mb-2 block">Stream</label>
            <Select value={selectedStream || 'all'} onValueChange={(v) => setSelectedStream(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="stream-select"><SelectValue placeholder="All Streams" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Streams</SelectItem>
                {availableStreams.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="text-sm font-medium mb-2 block">Questions</label>
          <Select value={questionCount} onValueChange={setQuestionCount}>
            <SelectTrigger data-testid="question-count"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[5, 10, 15, 20, 25, 30].map(n => <SelectItem key={n} value={String(n)}>{n} Questions</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Subject Grid */}
      <div>
        <label className="text-sm font-medium mb-3 block">Select Subject</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="subject-grid">
          {subjects.map(s => (
            <button key={s} onClick={() => setSelectedSubject(s)}
              data-testid={`subject-${s.toLowerCase().replace(/\s+/g, '-')}`}
              className={`p-3 rounded-xl border-2 transition-all text-left ${
                selectedSubject === s ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10' : 'border-border hover:border-primary/40 hover:bg-muted/50'
              }`}>
              <span className="text-xl block mb-0.5">{SUBJECT_ICONS[s] || '📚'}</span>
              <span className="text-xs font-medium">{s}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Exam Type */}
      <div>
        <label className="text-sm font-medium mb-2 block">Exam Type</label>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setExamType('mcq')} data-testid="type-mcq"
            className={`p-4 rounded-xl border-2 text-center transition-all ${examType === 'mcq' ? 'border-blue-500 bg-blue-500/5' : 'border-border hover:border-blue-300'}`}>
            <CheckCircle className="w-6 h-6 mx-auto mb-1 text-blue-500" />
            <span className="text-sm font-medium">MCQ</span>
            <p className="text-xs text-muted-foreground mt-0.5">Multiple Choice</p>
          </button>
          <button onClick={() => setExamType('written')} data-testid="type-written"
            className={`p-4 rounded-xl border-2 text-center transition-all ${examType === 'written' ? 'border-amber-500 bg-amber-500/5' : 'border-border hover:border-amber-300'}`}>
            <PenLine className="w-6 h-6 mx-auto mb-1 text-amber-500" />
            <span className="text-sm font-medium">Written</span>
            <p className="text-xs text-muted-foreground mt-0.5">AI Auto-Graded</p>
          </button>
        </div>
      </div>

      {/* Exam Mode Selection */}
      <div>
        <label className="text-sm font-medium mb-2 block">Exam Mode</label>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => setExamMode('quick')} data-testid="mode-quick"
            className={`p-5 rounded-xl border-2 text-left transition-all ${examMode === 'quick' ? 'border-violet-500 bg-violet-500/5 shadow-lg' : 'border-border hover:border-violet-300'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-sm font-bold block">Quick Answer Mode</span>
                <span className="text-xs text-muted-foreground">Learn as you go</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Check each answer instantly with explanations. Great for learning and practice.</p>
          </button>
          <button onClick={() => setExamMode('real')} data-testid="mode-real"
            className={`p-5 rounded-xl border-2 text-left transition-all ${examMode === 'real' ? 'border-red-500 bg-red-500/5 shadow-lg' : 'border-border hover:border-red-300'}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-sm font-bold block">Real Exam Mode</span>
                <span className="text-xs text-muted-foreground">Simulate real exam</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">No answers shown until you submit. Timed, just like a real board exam.</p>
          </button>
        </div>
      </div>

      <Button onClick={generateExam} disabled={generating || !selectedSubject}
        className="w-full h-12 text-base font-semibold bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
        data-testid="generate-exam-btn">
        {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating with AI...</>
          : <><Brain className="w-5 h-5 mr-2" />Generate {examMode === 'quick' ? 'Quick' : 'Real'} {examType.toUpperCase()} Exam</>}
      </Button>

      {/* History link */}
      <Button variant="outline" className="w-full" onClick={() => { loadHistory(); setView('history'); }} data-testid="view-history-btn">
        <History className="w-4 h-4 mr-2" />View Exam History
      </Button>
    </div>
  );

  // ====================== EXAM VIEW ======================
  const ExamView = () => {
    if (!currentExam) return null;
    const q = currentExam.questions[currentQ];
    if (!q) return null;
    const isChecked = !!checkedAnswers[currentQ];
    const quickScore = getQuickScore();

    return (
      <div className="flex gap-6 min-h-[calc(100vh-120px)]" style={{ zoom: fontScale }}>
        {/* LEFT: Question Navigator (Diamond Grid) */}
        <div className="w-64 shrink-0 flex flex-col self-start sticky top-4">
          {/* Timer */}
          <div className={`mb-4 p-3 rounded-xl text-center font-mono text-xl font-bold ${
            timeLeft !== null && timeLeft < 120
              ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 animate-pulse'
              : 'bg-muted text-foreground'
          }`} data-testid="exam-timer">
            <Clock className="w-5 h-5 inline-block mr-2 mb-0.5" />
            {formatTime(timeLeft)}
          </div>

          {/* Info bar */}
          <div className="mb-3 p-3 rounded-xl bg-muted/50 border text-xs space-y-1">
            <p className="font-medium">{selectedBoard} Class {selectedGrade}</p>
            <p className="text-muted-foreground">{selectedSubject} | {examType.toUpperCase()}</p>
            <p className="text-muted-foreground">{examMode === 'quick' ? 'Quick Answer' : 'Real Exam'} Mode</p>
            {examMode === 'quick' && quickScore && (
              <p className="font-bold text-primary">{quickScore.correct}/{quickScore.total} correct ({quickScore.percentage}%)</p>
            )}
          </div>

          {/* Diamond Grid */}
          <div className="flex-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Questions</p>
            <div className="grid grid-cols-4 gap-3 px-2" data-testid="question-navigator">
              {currentExam.questions.map((_, i) => (
                <DiamondNumber
                  key={i}
                  number={i + 1}
                  status={getQStatus(i)}
                  isActive={i === currentQ}
                  onClick={() => setCurrentQ(i)}
                  testId={`q-nav-${i}`}
                />
              ))}
            </div>

            {/* Legend */}
            <div className="mt-4 px-2 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground mb-1">Legend</p>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-sky-400 flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}>
                  <span className="text-[8px] font-bold text-blue-800">1</span>
                </div>
                <span className="text-[10px] text-muted-foreground">Unattempted</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 bg-[#FFD700] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}>
                  <span className="text-[8px] font-bold text-white">2</span>
                </div>
                <span className="text-[10px] text-muted-foreground">Attempted</span>
              </div>
              {examMode === 'quick' && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-green-500 flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}>
                      <span className="text-[8px] font-bold text-yellow-300">3</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">Correct</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-red-500 flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}>
                      <span className="text-[8px] font-bold text-yellow-300">4</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">Wrong</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Submit / Finish Buttons */}
          <div className="mt-4 space-y-2">
            {examMode === 'real' && (
              <Button onClick={handleSubmit} disabled={submitting || answeredCount === 0}
                className="w-full bg-gradient-to-r from-emerald-500 to-green-600" data-testid="submit-exam-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Grading...</> : <><Send className="w-4 h-4 mr-2" />Submit Exam</>}
              </Button>
            )}
            {examMode === 'quick' && (
              <Button onClick={() => {
                // In quick mode, calculate results from checked answers
                const totalChecked = Object.keys(checkedAnswers).length;
                if (totalChecked === 0) { toast.error('Check at least one answer first'); return; }
                const qs = getQuickScore();
                setResult({
                  percentage: qs?.percentage || 0,
                  total_score: qs?.correct || 0,
                  max_score: totalQ,
                  passed: (qs?.percentage || 0) >= 33,
                  exam_type: examType,
                  results: Object.entries(checkedAnswers).map(([idx, data]) => ({
                    question_index: parseInt(idx),
                    correct: data.correct,
                    explanation: data.explanation || '',
                  }))
                });
                setView('result');
              }} className="w-full bg-gradient-to-r from-violet-500 to-purple-600" data-testid="finish-quick-btn">
                <Trophy className="w-4 h-4 mr-2" />Finish & See Results
              </Button>
            )}
            <Button variant="outline" size="sm" className="w-full" onClick={() => { setView('setup'); setCurrentExam(null); }}>
              <ArrowLeft className="w-3 h-3 mr-1" />Exit Exam
            </Button>
          </div>
        </div>

        {/* RIGHT: Current Question */}
        <div className="flex-1">
          <div className="p-6 rounded-2xl border bg-card shadow-sm">
            {/* Question Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{currentQ + 1}</span>
                </div>
                <div>
                  <span className="text-sm font-semibold">Question {currentQ + 1} of {totalQ}</span>
                  {currentExam.exam_type === 'written' && q.marks && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">{q.marks} marks</span>
                  )}
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                examMode === 'quick' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {examMode === 'quick' ? 'Quick Mode' : 'Real Exam'}
              </span>
            </div>

            {/* Phase 40.31 — Font size slider (persistent, proportionate zoom) */}
            <div className="flex justify-end mb-3">
              <FontSizeSlider scale={fontScale} onChange={setFontScale} />
            </div>

            {/* Question Text */}
            <h3 className="text-lg font-semibold mb-6 leading-relaxed" data-testid="current-question">{q.question}</h3>

            {/* Answer Area */}
            <div data-testid="answer-area">
              {currentExam.exam_type === 'mcq' ? (
                <RadioGroup
                  value={String(answers[currentQ] ?? '')}
                  onValueChange={(v) => handleMCQAnswer(currentQ, parseInt(v))}
                  className="space-y-3"
                >
                  {q.options?.map((opt, oi) => {
                    let optClass = 'border-border hover:border-primary/40';
                    if (answers[currentQ] === oi && !isChecked) {
                      optClass = 'border-primary bg-primary/5';
                    }
                    if (isChecked && examMode === 'quick') {
                      if (oi === checkedAnswers[currentQ]?.correctAnswer) {
                        optClass = 'border-green-500 bg-green-50 dark:bg-green-950/30';
                      } else if (answers[currentQ] === oi && !checkedAnswers[currentQ]?.correct) {
                        optClass = 'border-red-500 bg-red-50 dark:bg-red-950/30';
                      }
                    }
                    return (
                      <div key={oi}
                        className={`flex items-center space-x-3 p-3.5 rounded-xl border-2 transition-all ${isChecked ? '' : 'cursor-pointer'} ${optClass}`}
                        onClick={() => !isChecked && handleMCQAnswer(currentQ, oi)}>
                        <RadioGroupItem value={String(oi)} id={`opt-${currentQ}-${oi}`} disabled={isChecked} />
                        <Label htmlFor={`opt-${currentQ}-${oi}`} className={`flex-1 text-sm ${isChecked ? '' : 'cursor-pointer'}`}>
                          <span className="font-medium mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                        </Label>
                        {isChecked && examMode === 'quick' && oi === checkedAnswers[currentQ]?.correctAnswer && (
                          <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                        )}
                        {isChecked && examMode === 'quick' && answers[currentQ] === oi && oi !== checkedAnswers[currentQ]?.correctAnswer && (
                          <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </RadioGroup>
              ) : (
                <>
                  <textarea
                    ref={writtenAnswerRef}
                    value={answers[currentQ] || ''}
                    onChange={(e) => handleWrittenAnswer(currentQ, e.target.value)}
                    onFocus={() => { wasWritingRef.current = true; }}
                    onBlur={() => { wasWritingRef.current = false; }}
                    disabled={isChecked && examMode === 'quick'}
                    placeholder="Write your answer here..."
                    className="w-full h-48 p-4 rounded-xl border-2 bg-background resize-y text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    data-testid="written-answer-input"
                  />
                  {/* Phase 40.27 — OCR handwritten answer sheet */}
                  <HandwrittenUploader
                    key={`ocr-${currentQ}`}
                    examId={currentExam.exam_id}
                    questionIndex={currentQ}
                    examSource="board"
                    onGraded={(g) => {
                      // Auto-fill the textarea with the OCR'd text so the
                      // student can see + confirm what the AI read.
                      handleWrittenAnswer(currentQ, g.transcribed_text || '');
                      setOcrResults(p => ({ ...p, [currentQ]: g }));
                      toast.success(`Graded: ${g.marks_awarded}/${g.max_marks} marks (${g.percentage}%)`);
                    }}
                  />
                  {ocrResults[currentQ] && (
                    <div className="mt-3 p-3 rounded-xl border-2 border-primary/40 bg-primary/5" data-testid="ocr-grade-panel">
                      <p className="text-sm font-semibold mb-1">AI grade: {ocrResults[currentQ].marks_awarded}/{ocrResults[currentQ].max_marks} ({ocrResults[currentQ].percentage}%)</p>
                      <p className="text-xs text-muted-foreground italic">"{ocrResults[currentQ].feedback}"</p>
                      {ocrResults[currentQ].improvements && (
                        <p className="text-xs mt-1"><strong>Improve:</strong> {ocrResults[currentQ].improvements}</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Quick Mode: Check Answer Button & Explanation */}
              {examMode === 'quick' && !isChecked && answers[currentQ] !== undefined && answers[currentQ] !== '' && (
                <Button onClick={() => checkAnswer(currentQ)}
                  className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="check-answer-btn">
                  <Eye className="w-4 h-4 mr-2" />Check Answer
                </Button>
              )}

              {isChecked && examMode === 'quick' && (
                <div className={`mt-4 p-4 rounded-xl border-2 ${
                  checkedAnswers[currentQ]?.correct === true
                    ? 'border-green-400 bg-green-50/50 dark:bg-green-950/20'
                    : checkedAnswers[currentQ]?.correct === false
                      ? 'border-red-400 bg-red-50/50 dark:bg-red-950/20'
                      : 'border-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
                } `} data-testid="answer-explanation">
                  <div className="flex items-center gap-2 mb-2">
                    {checkedAnswers[currentQ]?.correct === true && <><CheckCircle className="w-5 h-5 text-green-500" /><span className="font-bold text-green-600 text-sm">Correct!</span></>}
                    {checkedAnswers[currentQ]?.correct === false && <><XCircle className="w-5 h-5 text-red-500" /><span className="font-bold text-red-600 text-sm">Incorrect</span></>}
                    {checkedAnswers[currentQ]?.correct === null && <><BookOpen className="w-5 h-5 text-blue-500" /><span className="font-bold text-blue-600 text-sm">Model Answer</span></>}
                  </div>

                  {/* Concept heading */}
                  {checkedAnswers[currentQ]?.concept && (
                    <div className="mb-2">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                        <Brain className="w-3 h-3" />Concept: {checkedAnswers[currentQ].concept}
                      </span>
                    </div>
                  )}

                  {checkedAnswers[currentQ]?.explanation && (
                    <p className="text-sm text-muted-foreground mb-3">{checkedAnswers[currentQ].explanation}</p>
                  )}

                  {/* Phase 40.30 — Step-by-step "How to solve" panel */}
                  {checkedAnswers[currentQ]?.steps?.length > 0 && (
                    <div className="mt-3 border-t pt-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          const willOpen = !expandedSteps[currentQ];
                          setExpandedSteps(p => ({ ...p, [currentQ]: !p[currentQ] }));
                          if (willOpen) {
                            // Wait for the drawer to render, then bring it
                            // into view with the natural page scroll.
                            requestAnimationFrame(() => requestAnimationFrame(() => {
                              const drawer = document.querySelector('[data-testid="steps-drawer"]');
                              drawer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }));
                          }
                        }}
                        className="text-xs font-semibold text-primary hover:underline flex items-center gap-1"
                        data-testid="toggle-steps-btn"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {expandedSteps[currentQ] ? 'Hide' : 'Show'} step-by-step solution
                      </button>
                      {expandedSteps[currentQ] && (
                        <div
                          className="mt-3 space-y-2 bg-background/60 rounded-lg p-3 border"
                          data-testid="steps-drawer"
                        >
                          <p className="text-xs font-bold uppercase tracking-wider text-primary">How we solve this</p>
                          <ol className="space-y-1.5 text-sm list-none">
                            {checkedAnswers[currentQ].steps.map((step, si) => (
                              <li key={si} className="flex gap-2" data-testid={`step-${si}`}>
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold flex items-center justify-center">{si + 1}</span>
                                <span className="flex-1">{step}</span>
                              </li>
                            ))}
                          </ol>

                          {/* Why other options are wrong */}
                          {checkedAnswers[currentQ]?.whyWrong && Object.keys(checkedAnswers[currentQ].whyWrong).length > 0 && (
                            <div className="mt-3 pt-2 border-t border-border/50">
                              <p className="text-xs font-bold uppercase tracking-wider text-red-500 mb-1.5">Why the other options are wrong</p>
                              <ul className="space-y-1 text-xs text-muted-foreground">
                                {Object.entries(checkedAnswers[currentQ].whyWrong).map(([idx, why]) => (
                                  <li key={idx} className="flex gap-2">
                                    <span className="font-bold text-red-500">{String.fromCharCode(65 + Number(idx))}.</span>
                                    <span>{why}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Learning tip */}
                          {checkedAnswers[currentQ]?.learningTip && (
                            <div className="mt-3 pt-2 border-t border-border/50">
                              <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-1">💡 Remember for next time</p>
                              <p className="text-xs text-muted-foreground italic">{checkedAnswers[currentQ].learningTip}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {checkedAnswers[currentQ]?.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-xs font-medium">Key concepts:</span>
                      {checkedAnswers[currentQ].keywords.map((kw, ki) => (
                        <span key={ki} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{kw}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button variant="outline" disabled={currentQ === 0} onClick={() => setCurrentQ(p => p - 1)} data-testid="prev-q-btn">
                <ChevronLeft className="w-4 h-4 mr-1" />Previous
              </Button>
              <Button disabled={currentQ >= totalQ - 1} onClick={() => setCurrentQ(p => p + 1)} data-testid="next-q-btn">
                Next<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ====================== RESULT VIEW ======================
  const ResultView = () => {
    if (!result) return null;
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className={`text-center p-8 rounded-2xl border-2 ${result.passed ? 'border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-red-500/30 bg-red-50/50 dark:bg-red-950/20'}`}>
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${result.passed ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-red-100 dark:bg-red-900/40'}`}>
            {result.passed ? <Trophy className="w-10 h-10 text-emerald-600" /> : <XCircle className="w-10 h-10 text-red-500" />}
          </div>
          <h2 className="text-3xl font-bold mb-1" data-testid="result-percentage">{result.percentage}%</h2>
          <p className="text-muted-foreground mb-2">{result.total_score} / {result.max_score}</p>
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${result.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {result.passed ? 'PASSED' : 'NEEDS IMPROVEMENT'}
          </span>
        </div>

        {/* Detailed Review */}
        {result.results?.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Detailed Review</h3>
            {result.results.map((r, i) => {
              // Phase 40.26 — read questions from the submission itself when
              // navigating from History (currentExam is null there).
              const qList = currentExam?.questions || result.questions || [];
              const qData = qList[r.question_index];
              return (
                <div key={i} className={`p-4 rounded-xl border ${
                  r.correct === true ? 'border-emerald-200 bg-emerald-50/30 dark:border-emerald-800 dark:bg-emerald-950/20'
                    : r.correct === false ? 'border-red-200 bg-red-50/30 dark:border-red-800 dark:bg-red-950/20'
                      : 'border-border'
                }`} data-testid={`result-q-${r.question_index}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium flex-1">Q{r.question_index + 1}: {qData?.question || <em className="text-muted-foreground">Question text unavailable</em>}</span>
                    {r.unattempted && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0 ml-2">Not attempted</span>}
                    {!r.unattempted && r.correct === true && <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 ml-2" />}
                    {!r.unattempted && r.correct === false && <XCircle className="w-5 h-5 text-red-500 shrink-0 ml-2" />}
                    {r.marks_awarded !== undefined && <span className="text-sm font-bold text-primary shrink-0 ml-2">{r.marks_awarded}/{r.max_marks}</span>}
                  </div>
                  {/* Show correct answer for MCQs */}
                  {r.correct === false && qData?.options && (
                    <div className="mb-2 text-xs space-y-1">
                      <p className="text-red-500">Your answer: <span className="font-medium">{String.fromCharCode(65 + (r.your_answer ?? 0))}. {qData.options[r.your_answer]}</span></p>
                      <p className="text-emerald-600">Correct answer: <span className="font-medium">{String.fromCharCode(65 + (r.correct_answer ?? qData.correct))}. {qData.options[r.correct_answer ?? qData.correct]}</span></p>
                    </div>
                  )}
                  {r.correct === true && qData?.options && (
                    <div className="mb-2 text-xs">
                      <p className="text-emerald-600">Your answer: <span className="font-medium">{String.fromCharCode(65 + (r.your_answer ?? 0))}. {qData.options[r.your_answer]}</span></p>
                    </div>
                  )}
                  {/* Explanation */}
                  {(r.explanation || qData?.explanation) && (
                    <div className="mt-2 p-3 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                      <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1 flex items-center gap-1"><BookOpen className="w-3 h-3" /> Explanation</p>
                      <p className="text-sm text-blue-800 dark:text-blue-300">{r.explanation || qData?.explanation}</p>
                    </div>
                  )}
                  {r.feedback && (
                    <div className="mt-2 p-3 rounded-lg bg-muted/50">
                      <p className="text-xs font-semibold mb-1">Feedback</p>
                      <p className="text-sm">{r.feedback}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-3">
          <Button onClick={() => { setView('setup'); setCurrentExam(null); setResult(null); setCheckedAnswers({}); }} className="flex-1" data-testid="new-exam-btn">
            <Sparkles className="w-4 h-4 mr-2" />Take Another Exam
          </Button>
          <Button variant="outline" onClick={() => { loadHistory(); setView('history'); }}>
            <History className="w-4 h-4 mr-2" />History
          </Button>
        </div>
      </div>
    );
  };

  // ====================== HISTORY VIEW ======================
  const HistoryView = () => (
    <div className="max-w-3xl mx-auto space-y-4">
      <Button variant="ghost" onClick={() => setView('setup')}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
      <h2 className="text-xl font-bold">Exam History</h2>
      {historyLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : history.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No exam attempts yet.</p>
        </div>
      ) : (
        history.map((h, i) => (
          <div
            key={i}
            className="p-4 rounded-xl border hover:shadow-md transition-shadow cursor-pointer hover:border-primary/40"
            data-testid={`history-item-${i}`}
            onClick={() => {
              // Phase 40.26 — clicking a history row opens the FULL Q+A review
              // instead of just showing the score card.
              setResult(h);
              setCurrentExam(null);
              setView('result');
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{h.title || `${h.subject} - ${h.exam_type?.toUpperCase()}`}</h4>
                <p className="text-xs text-muted-foreground">
                  {h.board} Class {h.grade} | {new Date(h.submitted_at).toLocaleDateString()} · {h.results?.length || 0} questions
                </p>
              </div>
              <div className="text-right">
                <span className={`text-lg font-bold ${h.passed ? 'text-emerald-600' : 'text-red-500'}`}>{h.percentage}%</span>
                <p className="text-xs text-muted-foreground">{h.total_score}/{h.max_score}</p>
                <p className="text-[10px] text-primary mt-1">Tap to review →</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                // Phase 40.24 — mid-exam back should return to setup with a
                // confirm, not silently drop the user on the dashboard.
                if (view === 'exam') {
                  if (window.confirm('Exit this exam and go back to setup? Your current answers will be lost.')) {
                    setView('setup');
                    setCurrentExam(null);
                    setAnswers({});
                    setCheckedAnswers({});
                    setCurrentQ(0);
                  }
                } else if (view === 'result' || view === 'history') {
                  setView('setup');
                } else {
                  navigate('/dashboard');
                }
              }}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold">Exam Module</h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{selectedBoard} Class {selectedGrade}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full"><User className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')}><User className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className={`${view === 'exam' ? 'max-w-full px-6' : 'max-w-5xl mx-auto px-4'} py-6`}>
        {view === 'setup' && <SetupView />}
        {view === 'exam' && <ExamView />}
        {view === 'result' && <ResultView />}
        {view === 'history' && <HistoryView />}
      </main>
    </div>
  );
}
